import React, { useEffect, useMemo, useState } from 'react';
import { User } from '../types';
import { ArrowLeft, Key, MessageSquare, Mail, Calendar, Hash, Save, X, CheckCircle2, Volume2, Volume1, VolumeX, ShieldAlert, ChevronDown, ChevronUp, User as UserIcon, Upload, Trash2, LogOut } from 'lucide-react';
import { isLocalBackend, phpApiUrl, phpAuthHeaders } from '../apiEnv';

export type UserProfileTab = 'profile' | 'password' | 'feedback' | 'settings';

const USER_PROFILE_TAB_META: Record<UserProfileTab, {
  label: string;
  activeClassName: string;
  inactiveClassName: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
}> = {
  profile: {
    label: 'PROFILE / 个人资料',
    activeClassName: 'bg-blue-600/20 border border-blue-500 text-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.2)]',
    inactiveClassName: 'bg-transparent text-zinc-500 hover:bg-white/5 hover:text-zinc-300',
    icon: UserIcon,
  },
  password: {
    label: 'PASSWORD / 修改密码',
    activeClassName: 'bg-red-600/20 border border-red-500 text-red-400 shadow-[0_0_15px_rgba(239,68,68,0.2)]',
    inactiveClassName: 'bg-transparent text-zinc-500 hover:bg-white/5 hover:text-zinc-300',
    icon: Key,
  },
  settings: {
    label: 'SETTINGS / 设置',
    activeClassName: 'bg-cyan-600/20 border border-cyan-500 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.2)]',
    inactiveClassName: 'bg-transparent text-zinc-500 hover:bg-white/5 hover:text-zinc-300',
    icon: Volume2,
  },
  feedback: {
    label: 'FEEDBACK / 问题反馈',
    activeClassName: 'bg-green-600/20 border border-green-500 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.2)]',
    inactiveClassName: 'bg-transparent text-zinc-500 hover:bg-white/5 hover:text-zinc-300',
    icon: MessageSquare,
  },
};

interface UserProfileProps {
  user: User;
  onBack: () => void;
  onUpdateUser: (updatedUser: User) => void;
  onOpenAdmin: () => void;
  onLogout: () => void;
  volume: number;
  onVolumeChange: (vol: number) => void;
  initialTab?: UserProfileTab;
}

export const UserProfile: React.FC<UserProfileProps> = ({ user, onBack, onUpdateUser, onOpenAdmin, onLogout, volume, onVolumeChange, initialTab }) => {
  const [activeTab, setActiveTab] = useState<UserProfileTab | null>(initialTab ?? null);
  const [isMobileTabMenuOpen, setIsMobileTabMenuOpen] = useState(false);
  
  const [displayName, setDisplayName] = useState(user.displayName);
  const [avatarUrl, setAvatarUrl] = useState(user.avatarUrl || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [feedback, setFeedback] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{type: 'success'|'error', text: string} | null>(null);

  useEffect(() => {
    setActiveTab(initialTab ?? null);
    setMessage(null);
  }, [initialTab]);

  useEffect(() => {
    setDisplayName(user.displayName);
    setAvatarUrl(user.avatarUrl || '');
  }, [user.displayName, user.avatarUrl]);

  const activeTabMeta = activeTab ? USER_PROFILE_TAB_META[activeTab] : null;

  const handleTabChange = (tab: UserProfileTab) => {
    setActiveTab(tab);
    setMessage(null);
    setIsMobileTabMenuOpen(false);
  };

  const avatarInitial = useMemo(() => {
    const base = displayName.trim() || user.displayName || user.email || 'U';
    return base.charAt(0).toUpperCase();
  }, [displayName, user.displayName, user.email]);

  const handleAvatarFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please upload an image file / 请上传图片文件' });
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      setMessage({ type: 'error', text: 'Image must be smaller than 2MB / 头像图片需小于 2MB' });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      if (!result.startsWith('data:image/')) {
        setMessage({ type: 'error', text: 'Failed to read image / 图片读取失败' });
        return;
      }
      setAvatarUrl(result);
      setMessage(null);
    };
    reader.onerror = () => {
      setMessage({ type: 'error', text: 'Failed to read image / 图片读取失败' });
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim()) return;
    
    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch(isLocalBackend() ? '/api/user/profile' : phpApiUrl('update_profile'), {
        method: 'POST',
        headers: phpAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ displayName, avatarUrl })
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Profile updated successfully / 资料修改成功' });
        onUpdateUser({ ...user, displayName, avatarUrl });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error' });
    }
    setLoading(false);
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return;
    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match / 两次输入的密码不一致' });
      return;
    }
    
    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch(isLocalBackend() ? '/api/user/profile' : phpApiUrl('update_profile'), {
        method: 'POST',
        headers: phpAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Password updated successfully / 密码修改成功' });
        setPassword('');
        setConfirmPassword('');
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update password' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error' });
    }
    setLoading(false);
  };

  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim()) return;
    
    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch(isLocalBackend() ? '/api/feedback' : phpApiUrl('feedback'), {
        method: 'POST',
        headers: phpAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ content: feedback })
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: 'Feedback submitted successfully / 反馈提交成功' });
        setFeedback('');
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to submit feedback' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Network error' });
    }
    setLoading(false);
  };

  return (
    <div className="h-full w-full flex flex-col items-center justify-start relative overflow-y-auto text-white font-rajdhani">
      {activeTab === null ? (
        /* ── 选项列表模式 ── */
        <div className="flex w-full max-w-md flex-col gap-2 px-4 py-4">
          <h1 className="mb-2 text-center text-xl font-pingfang font-bold tracking-[0.06em] text-white">
            {user.displayName}
          </h1>
          {/* 用户 id 下方的头像：有自定义头像就显示，否则用首字母占位 */}
          <div className="mb-4 flex justify-center">
            <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border border-purple-400/30 bg-gradient-to-br from-purple-500/25 via-cyan-500/10 to-zinc-900 text-white shadow-[0_0_24px_rgba(168,85,247,0.18)]">
              {avatarUrl ? (
                <img src={avatarUrl} alt="用户头像" className="h-full w-full object-cover" />
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <UserIcon size={28} className="text-purple-200/90" />
                  <span className="mt-1 text-lg font-orbitron">{avatarInitial}</span>
                </div>
              )}
            </div>
          </div>
          {(Object.entries(USER_PROFILE_TAB_META) as Array<[UserProfileTab, (typeof USER_PROFILE_TAB_META)[UserProfileTab]]>).map(([tab, meta]) => {
            const Icon = meta.icon;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => handleTabChange(tab)}
                className="flex w-full items-center gap-4 px-5 py-4 text-left font-pingfang text-[16px] font-medium text-white transition-colors active:text-white/70"
              >
                <Icon size={22} className="shrink-0 text-cyan-400" />
                <span>{(tab === 'profile' ? '个人资料' : tab === 'password' ? '修改密码' : tab === 'settings' ? '设置' : '问题反馈')}</span>
                <ChevronDown size={16} className="ml-auto shrink-0 -rotate-90 text-zinc-600" />
              </button>
            );
          })}
          {user.isAdmin && (
            <button
              type="button"
              onClick={onOpenAdmin}
              className="flex w-full items-center gap-4 px-5 py-4 text-left font-pingfang text-[16px] font-medium text-red-300 transition-colors active:text-red-200"
            >
              <ShieldAlert size={22} className="shrink-0 text-red-400" />
              <span>管理后台</span>
              <ChevronDown size={16} className="ml-auto shrink-0 -rotate-90 text-red-600/60" />
            </button>
          )}
          <button
            type="button"
            onClick={onLogout}
            className="mt-2 flex w-full items-center gap-4 px-5 py-4 text-left font-pingfang text-[16px] font-medium text-red-300 transition-colors active:text-red-200"
          >
            <LogOut size={22} className="shrink-0 text-red-400" />
            <span>退出登录</span>
            <ChevronDown size={16} className="ml-auto shrink-0 -rotate-90 text-red-600/60" />
          </button>
        </div>
      ) : (
        /* ── 内容模式 ── */
        <div className="flex w-full max-w-4xl flex-col gap-6 p-3 md:p-6">
          {/* 返回按钮 + 标题 */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => { setActiveTab(null); setMessage(null); }}
              className="flex items-center gap-2 rounded-xl border border-white/10 bg-black/50 px-3 py-2.5 text-sm font-pingfang text-zinc-400 transition-colors active:text-white active:border-white/20"
            >
              <ArrowLeft size={18} />
              返回
            </button>
            <h1 className="font-pingfang text-lg font-semibold text-white">
              {activeTabMeta?.label?.split(' / ').pop() ?? ''}
            </h1>
          </div>

          {/* 内容区 */}
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/80 p-6 backdrop-blur-sm">
            {message && (
              <div className={`mb-6 rounded-xl p-4 flex items-center gap-3 font-pingfang text-sm ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/50 text-green-400' : 'bg-red-500/10 border border-red-500/50 text-red-400'}`}>
                {message.type === 'success' ? <CheckCircle2 size={18} /> : <X size={18} />}
                {message.text}
              </div>
            )}

            {activeTab === 'profile' && (
              <div className="space-y-6 animate-in fade-in">
                <form onSubmit={handleUpdateProfile}>
                  <h2 className="font-pingfang text-lg font-bold text-purple-400 border-b border-zinc-800 pb-3 mb-4">个人资料</h2>
                  <div className="space-y-4">
                    <div className="flex flex-col items-center gap-4 rounded-2xl border border-zinc-800 bg-black/40 px-4 py-5">
                      <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border border-purple-400/30 bg-gradient-to-br from-purple-500/25 via-cyan-500/10 to-zinc-900 text-white shadow-[0_0_24px_rgba(168,85,247,0.18)]">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt="用户头像预览" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex flex-col items-center justify-center">
                            <UserIcon size={28} className="text-purple-200/90" />
                            <span className="mt-1 text-lg font-orbitron">{avatarInitial}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
                        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-purple-500/40 bg-purple-500/10 px-4 py-2 text-sm font-pingfang text-purple-200 transition-colors hover:bg-purple-500/20">
                          <Upload size={16} />
                          上传头像
                          <input type="file" accept="image/*" className="hidden" onChange={handleAvatarFileChange} />
                        </label>
                        <button
                          type="button"
                          onClick={() => setAvatarUrl('')}
                          className="flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm font-pingfang text-zinc-300 transition-colors hover:border-white/20 hover:bg-white/10"
                        >
                          <Trash2 size={16} />
                          恢复默认头像
                        </button>
                      </div>
                      <p className="text-center text-xs text-zinc-500">支持常见图片格式，大小不超过 2MB。</p>
                    </div>
                    <div>
                      <label className="block text-zinc-400 font-pingfang text-sm mb-2">昵称</label>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="w-full bg-black/60 border border-zinc-700 rounded-xl py-3 px-4 text-white focus:border-purple-500 focus:outline-none transition-colors font-pingfang"
                        placeholder="请输入新昵称"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !displayName.trim() || (displayName === user.displayName && avatarUrl === (user.avatarUrl || ''))}
                    className="flex items-center justify-center gap-2 w-full py-4 mt-6 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-pingfang font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? '正在保存...' : <><Save size={18} /> 保存修改</>}
                  </button>
                </form>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-black/50 border border-zinc-800 rounded-xl p-4 flex items-center gap-4">
                    <div className="p-3 bg-blue-500/10 rounded-full text-blue-400"><Hash size={24} /></div>
                    <div>
                      <div className="text-zinc-500 font-pingfang text-xs">UID</div>
                      <div className="text-xl font-mono text-white">{user.uid}</div>
                    </div>
                  </div>
                  <div className="bg-black/50 border border-zinc-800 rounded-xl p-4 flex items-center gap-4">
                    <div className="shrink-0 p-3 bg-purple-500/10 rounded-full text-purple-400"><Mail size={24} /></div>
                    <div className="min-w-0 flex-1">
                      <div className="text-zinc-500 font-pingfang text-xs">邮箱</div>
                      <div className="text-base text-white break-all leading-snug">{user.email}</div>
                    </div>
                  </div>
                  <div className="bg-black/50 border border-zinc-800 rounded-xl p-4 flex items-center gap-4">
                    <div className="p-3 bg-green-500/10 rounded-full text-green-400"><Calendar size={24} /></div>
                    <div>
                      <div className="text-zinc-500 font-pingfang text-xs">注册时间</div>
                      <div className="text-lg text-white">{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'password' && (
              <form onSubmit={handleUpdatePassword} className="space-y-6 animate-in fade-in">
                <h2 className="font-pingfang text-lg font-bold text-red-400 border-b border-zinc-800 pb-4 mb-6">修改密码</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-zinc-400 font-pingfang text-sm mb-2">新密码</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-black/60 border border-zinc-700 rounded-xl py-3 px-4 text-white focus:border-red-500 focus:outline-none transition-colors font-pingfang"
                      placeholder="请输入新密码"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-400 font-pingfang text-sm mb-2">确认密码</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full bg-black/60 border border-zinc-700 rounded-xl py-3 px-4 text-white focus:border-red-500 focus:outline-none transition-colors font-pingfang"
                      placeholder="请再次输入新密码"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className="flex items-center justify-center gap-2 w-full py-4 bg-red-600 hover:bg-red-500 text-white rounded-xl font-pingfang font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? '正在更新...' : <><Key size={18} /> 更新密码</>}
                </button>
              </form>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-6 animate-in fade-in">
                <h2 className="font-pingfang text-lg font-bold text-cyan-400 border-b border-zinc-800 pb-4 mb-6">游戏设置</h2>
                <div className="bg-black/50 border border-zinc-800 rounded-xl p-6">
                  <label className="flex items-center gap-2 text-zinc-400 font-pingfang text-sm mb-6">
                    {volume === 0 ? <VolumeX size={18} /> : volume < 0.4 ? <Volume1 size={18} /> : <Volume2 size={18} />}
                    主音量
                  </label>
                  <div className="flex items-center gap-4">
                    <span className="text-zinc-500 font-mono text-xs">0%</span>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={volume}
                      onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
                      className="flex-1 h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                    />
                    <span className="text-zinc-500 font-mono text-xs">100%</span>
                  </div>
                  <div className="text-center mt-4">
                    <span className="text-cyan-400 font-mono text-xl">{Math.round(volume * 100)}%</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'feedback' && (
              <form onSubmit={handleFeedbackSubmit} className="space-y-6 animate-in fade-in">
                <h2 className="font-pingfang text-lg font-bold text-green-400 border-b border-zinc-800 pb-4 mb-6">问题反馈</h2>
                <div>
                  <label className="block text-zinc-400 font-pingfang text-sm mb-2">反馈内容</label>
                  <textarea
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    className="w-full bg-black/60 border border-zinc-700 rounded-xl py-3 px-4 text-white focus:border-green-500 focus:outline-none transition-colors min-h-[150px] resize-y font-pingfang"
                    placeholder="告诉我们你发现的问题或建议..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !feedback.trim()}
                  className="flex items-center justify-center gap-2 w-full py-4 bg-green-600 hover:bg-green-500 text-white rounded-xl font-pingfang font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? '正在提交...' : <><MessageSquare size={18} /> 提交反馈</>}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
